import { useEffect, useState, useRef, useMemo } from "react";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Plus, Trash2, Paperclip, Wallet, TrendingUp, TrendingDown, X, Filter, Users, List, Download } from "lucide-react";
import BulkActionBar from "../components/BulkActionBar";

const CURR = ["INR", "USD", "EUR", "GBP"];
const SYM = { INR: "₹", USD: "$", EUR: "€", GBP: "£" };
const METHODS = ["cash", "upi", "bank", "card", "other"];
const MAX_ATTACH_KB = 200; // <200 kb per user spec

export default function ReceiptsPayments() {
  const { user } = useAuth();
  const isAdmin = user.effective_role === "FOUNDER" || user.effective_role === "BDOM";

  const [rows, setRows] = useState([]);
  const [balance, setBalance] = useState({});
  const [allBalances, setAllBalances] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterUser, setFilterUser] = useState("");
  // "list" — flat ledger (current view). "staff" — grouped by team member
  // with per-currency subtotals, so Founder/BDOM can eyeball each staffer's
  // collections at a glance without hunting through the flat list.
  const [view, setView] = useState("list");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ kind: "receipt", currency: "INR", method: "cash", amount: "", description: "", entry_date: new Date().toISOString().slice(0, 10) });
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const toggleSel = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const fileRef = useRef();

  const load = async () => {
    const q = isAdmin && filterUser ? `?user_id=${filterUser}` : "";
    const [entriesRes, myBalRes] = await Promise.all([
      api.get(`/receipts-payments${q}`),
      api.get("/receipts-payments/balance/me"),
    ]);
    setRows(entriesRes.data);
    setBalance(myBalRes.data.balance);
    if (isAdmin) {
      const [balRes, usersRes] = await Promise.all([
        api.get("/receipts-payments/balances"),
        api.get("/users"),
      ]);
      setAllBalances(balRes.data);
      setUsers(usersRes.data);
    }
  };
  useEffect(() => { load(); }, [filterUser]);

  const pickFile = () => fileRef.current?.click();
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_ATTACH_KB * 1024) {
      toast.error(`Attachment must be under ${MAX_ATTACH_KB} KB`);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setAttachment(f);
  };

  const submit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error("Enter a positive amount");
    let file_id = null;
    if (attachment) {
      setUploading(true);
      try {
        const fd = new FormData(); fd.append("file", attachment);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        file_id = data.id;
      } catch {
        toast.error("Attachment upload failed"); setUploading(false); return;
      }
      setUploading(false);
    }
    await api.post("/receipts-payments", { ...form, amount: parseFloat(form.amount), file_id });
    setForm({ kind: "receipt", currency: "INR", method: "cash", amount: "", description: "", entry_date: new Date().toISOString().slice(0, 10) });
    setAttachment(null);
    if (fileRef.current) fileRef.current.value = "";
    setAdding(false);
    toast.success("Entry saved");
    await load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/receipts-payments/${id}`);
    await load();
  };

  // Bulk-delete — routes through the Recycle-Bin snapshot path on the
  // backend so a mistaken sweep is one-tap restorable from /recycle.
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    await api.post("/receipts-payments/bulk-delete", { ids });
    setSelected(new Set());
    await load();
  };
  const toggleSelectAll = () => {
    const visibleIds = rows.map(r => r.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach(id => next.delete(id));
      else visibleIds.forEach(id => next.add(id));
      return next;
    });
  };

  const totalsByCur = useMemo(() => {
    const t = {};
    for (const c of CURR) t[c] = { receipt: 0, payment: 0 };
    for (const r of rows) {
      const c = r.currency || "INR";
      if (!t[c]) t[c] = { receipt: 0, payment: 0 };
      t[c][r.kind] += Number(r.amount) || 0;
    }
    return t;
  }, [rows]);

  // Group ledger rows by staff for the "By Staff" tab — one card per team
  // member with their entries, receipt/payment subtotals per currency and
  // the running balance the backend already sends via `allBalances`.
  const rowsByStaff = useMemo(() => {
    if (!isAdmin) return [];
    const groups = new Map();
    for (const r of rows) {
      const uid = r.user_id || "unknown";
      if (!groups.has(uid)) {
        groups.set(uid, {
          user_id: uid,
          name: r.user_name || "Unknown",
          role: r.user_role || "",
          entries: [],
          totals: {}, // { INR: {receipt, payment}, ... }
        });
      }
      const g = groups.get(uid);
      g.entries.push(r);
      const c = r.currency || "INR";
      if (!g.totals[c]) g.totals[c] = { receipt: 0, payment: 0 };
      g.totals[c][r.kind] += Number(r.amount) || 0;
    }
    // Sort staff by biggest INR net (receipts − payments) so top collectors surface first.
    return [...groups.values()].sort((a, b) => {
      const na = (a.totals.INR?.receipt || 0) - (a.totals.INR?.payment || 0);
      const nb = (b.totals.INR?.receipt || 0) - (b.totals.INR?.payment || 0);
      return nb - na;
    });
  }, [rows, isAdmin]);

  const balanceFor = (uid) => (allBalances || []).find(x => x.user_id === uid)?.balance || {};

  // Per-staff CSV export — one file for the whole team (all groups). The
  // Founder can hand it to Finance for offline reconciliation without
  // scraping the UI row-by-row. Client-side generation keeps the endpoint
  // count low.
  const exportStaffCSV = () => {
    const header = ["Staff", "Role", "Date", "Type", "Method", "Currency", "Amount", "Description", "Bill?"];
    const lines = [header.join(",")];
    const esc = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    for (const g of rowsByStaff) {
      for (const r of g.entries) {
        lines.push([
          esc(g.name), esc(g.role), esc(r.entry_date || ""), esc(r.kind || ""),
          esc(r.method || ""), esc(r.currency || ""), esc(r.amount || 0),
          esc(r.description || ""), esc(r.file_id ? "yes" : ""),
        ].join(","));
      }
      // Subtotal row per staff — makes the CSV skim-friendly in Excel.
      for (const [c, t] of Object.entries(g.totals)) {
        const net = (balanceFor(g.user_id)[c] ?? (t.receipt - t.payment));
        lines.push([esc(g.name), esc(g.role), "", "SUBTOTAL", "", esc(c), esc(net), esc(`Rec ${t.receipt} · Pay ${t.payment}`), ""].join(","));
      }
    }
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `receipts-by-staff-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    toast.success(`Exported ${rowsByStaff.length} staff group${rowsByStaff.length === 1 ? "" : "s"}`);
  };

  const openAttachment = (fid) => {
    const token = getToken();
    window.open(`${API}/files/${fid}?auth=${token}`, "_blank");
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Finance</div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Wallet className="text-brand" /> Receipts & Payments</h1>
          <p className="text-sm text-[color:var(--aii-text-2)] mt-1">Log every cash / digital receipt &amp; payment. Attach the bill or screenshot (max 200 KB).</p>
        </div>
        <button data-testid="rp-add" onClick={() => setAdding(true)} className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5"><Plus size={14} /> New entry</button>
      </div>

      {/* Balance cards — for admins (Founder/BDOM) the tile shows the
          NET balance owed to (or by) the entire team. For non-admins it's
          their personal balance. Fixes the "MY BALANCE ₹0" bug when the
          founder had ledger entries logged against other team members. */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {CURR.map(c => {
          const teamBal = isAdmin
            ? (allBalances || []).filter(x => x.user_id !== user?.id).reduce((s, x) => s + (Number(x.balance?.[c]) || 0), 0)
            : null;
          const b = isAdmin ? teamBal : (balance[c] ?? 0);
          const neg = b < 0;
          const label = isAdmin ? `Balance with staff · ${c}` : `My balance · ${c}`;
          const helpText = isAdmin
            ? (b > 0 ? "Owed to staff" : b < 0 ? "Owed by staff" : "Settled")
            : `Receipts ${SYM[c]}${totalsByCur[c].receipt.toLocaleString()} · Payments ${SYM[c]}${totalsByCur[c].payment.toLocaleString()}`;
          return (
            <div key={c} data-testid={`rp-bal-${c}`} className={`aii-card p-4 ${neg ? "border-red-300 bg-red-50" : ""}`}>
              <div className="text-xs text-[color:var(--aii-muted)] uppercase tracking-widest">{label}</div>
              <div className={`text-2xl font-display font-semibold mt-1 ${neg ? "text-red-700" : "text-brand"}`}>{SYM[c]}{Number(b).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
              <div className="text-[11px] text-[color:var(--aii-muted)] mt-1">{helpText}</div>
            </div>
          );
        })}
      </div>

      {/* Admin: team balances */}
      {isAdmin && (
        <div className="aii-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Team ledger balances</h3>
            <div className="flex items-center gap-2 text-sm">
              <Filter size={14} className="text-[color:var(--aii-muted)]" />
              <select data-testid="rp-filter-user" value={filterUser} onChange={e => setFilterUser(e.target.value)} className="px-2 py-1.5 border border-[color:var(--aii-border)] rounded-md bg-white">
                <option value="">All members&apos; entries</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="aii-table">
              <thead><tr><th>Member</th><th>Role</th>{CURR.map(c => <th key={c} className="text-right">{c}</th>)}</tr></thead>
              <tbody>
                {allBalances.map(b => (
                  <tr key={b.user_id}>
                    <td className="font-medium">{b.name}</td>
                    <td>{b.role}</td>
                    {CURR.map(c => {
                      const v = b.balance?.[c] ?? 0;
                      return <td key={c} className={`text-right ${v < 0 ? "text-red-600 font-semibold" : ""}`}>{SYM[c]}{v.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add drawer */}
      {adding && (
        <div className="aii-card p-5" data-testid="rp-form">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold">New ledger entry</h3>
            <button onClick={() => setAdding(false)}><X size={16} /></button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Type</label>
              <div className="flex gap-2 mt-1">
                {["receipt", "payment"].map(k => (
                  <button key={k} data-testid={`rp-kind-${k}`} onClick={() => setForm(f => ({ ...f, kind: k }))} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border text-sm font-semibold ${form.kind === k ? (k === "receipt" ? "bg-emerald-600 text-white border-emerald-600" : "bg-red-600 text-white border-red-600") : "bg-white border-[color:var(--aii-border)]"}`}>
                    {k === "receipt" ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                    {k === "receipt" ? "Receipt (in)" : "Payment (out)"}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Currency</label>
              <select data-testid="rp-currency" value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
                {CURR.map(c => <option key={c} value={c}>{SYM[c]} {c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Amount</label>
              <input data-testid="rp-amount" type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Method</label>
              <select data-testid="rp-method" value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
                {METHODS.map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Date</label>
              <input data-testid="rp-date" type="date" value={form.entry_date} onChange={e => setForm(f => ({ ...f, entry_date: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" />
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Description</label>
              <input data-testid="rp-desc" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" placeholder="e.g. Airport pickup — Rahul, taxi bill" />
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="text-xs uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Attach bill / screenshot (max {MAX_ATTACH_KB} KB)</label>
              <div className="flex items-center gap-2 mt-1">
                <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" data-testid="rp-file-input" />
                <button onClick={pickFile} type="button" className="flex items-center gap-1.5 px-3 py-2 border border-[color:var(--aii-border)] bg-white rounded-md hover:bg-[color:var(--aii-green-tint)] text-sm"><Paperclip size={14} /> {attachment ? attachment.name : "Choose file"}</button>
                {attachment && <button onClick={() => { setAttachment(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-xs text-[color:var(--aii-muted)]">Remove</button>}
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button data-testid="rp-save" disabled={uploading} onClick={submit} className="bg-brand text-white font-semibold px-5 py-2 rounded-md hover:bg-brand-dark disabled:opacity-50">{uploading ? "Uploading…" : "Save entry"}</button>
          </div>
        </div>
      )}

      {/* Ledger list */}
      {isAdmin && (
        <div className="flex items-center gap-1 border-b border-[color:var(--aii-border)]" data-testid="rp-view-tabs">
          <button
            data-testid="rp-tab-list"
            onClick={() => setView("list")}
            className={`px-3 py-2 text-sm font-semibold flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${view === "list" ? "border-brand text-brand" : "border-transparent text-[color:var(--aii-text-2)] hover:text-brand"}`}>
            <List size={14}/> Ledger
          </button>
          <button
            data-testid="rp-tab-staff"
            onClick={() => setView("staff")}
            className={`px-3 py-2 text-sm font-semibold flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${view === "staff" ? "border-brand text-brand" : "border-transparent text-[color:var(--aii-text-2)] hover:text-brand"}`}>
            <Users size={14}/> By Staff
          </button>
        </div>
      )}

      {view === "staff" && isAdmin ? (
        <div className="space-y-4" data-testid="rp-by-staff">
          <div className="flex items-center justify-end">
            <button
              data-testid="rp-staff-export-csv"
              onClick={exportStaffCSV}
              disabled={rowsByStaff.length === 0}
              className="text-xs px-3 py-1.5 border border-brand text-brand rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold flex items-center gap-1.5 disabled:opacity-50"
              title="Download every staff member's ledger + subtotals for offline reconciliation">
              <Download size={12}/> Export CSV
            </button>
          </div>
          {rowsByStaff.length === 0 && (
            <div className="aii-card p-8 text-center text-[color:var(--aii-muted)]">No entries yet for any staff member.</div>
          )}
          {rowsByStaff.map(g => {
            const bal = balanceFor(g.user_id);
            return (
              <div key={g.user_id} className="aii-card p-5" data-testid={`rp-staff-${g.user_id}`}>
                <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                  <div>
                    <div className="font-semibold text-base">{g.name} <span className="text-xs font-normal text-[color:var(--aii-muted)]">({g.role})</span></div>
                    <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5">{g.entries.length} entr{g.entries.length === 1 ? "y" : "ies"}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {CURR.filter(c => g.totals[c] || bal[c]).map(c => {
                      const t = g.totals[c] || { receipt: 0, payment: 0 };
                      const net = (bal[c] ?? (t.receipt - t.payment));
                      const neg = net < 0;
                      return (
                        <div key={c} data-testid={`rp-staff-tot-${g.user_id}-${c}`} className={`px-3 py-1.5 rounded-md border ${neg ? "border-red-300 bg-red-50" : "border-[color:var(--aii-border)] bg-[color:var(--aii-green-tint)]/40"}`}>
                          <div className="text-[9px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">{c}</div>
                          <div className={`text-sm font-semibold ${neg ? "text-red-700" : "text-brand"}`}>{SYM[c]}{Number(net).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                          <div className="text-[10px] text-[color:var(--aii-muted)]">+{SYM[c]}{t.receipt.toLocaleString()} · −{SYM[c]}{t.payment.toLocaleString()}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="aii-table">
                    <thead><tr>
                      <th>Date</th><th>Type</th><th>Method</th><th className="text-right">Amount</th><th>Description</th><th>Bill</th>
                    </tr></thead>
                    <tbody>
                      {g.entries.map(r => (
                        <tr key={r.id} data-testid={`rp-staff-row-${r.id}`}>
                          <td>{r.entry_date}</td>
                          <td>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${r.kind === "receipt" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                              {r.kind === "receipt" ? <TrendingUp size={11} /> : <TrendingDown size={11} />} {r.kind}
                            </span>
                          </td>
                          <td className="uppercase text-xs">{r.method}</td>
                          <td className={`text-right font-semibold ${r.kind === "receipt" ? "text-emerald-700" : "text-red-700"}`}>
                            {r.kind === "receipt" ? "+" : "−"}{SYM[r.currency] || ""}{Number(r.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                          </td>
                          <td>{r.description || "—"}</td>
                          <td>{r.file_id ? <button onClick={() => openAttachment(r.file_id)} className="text-brand hover:underline text-xs inline-flex items-center gap-1"><Paperclip size={12} /> view</button> : <span className="text-[color:var(--aii-muted)] text-xs">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      <div className="aii-card overflow-x-auto">
        <BulkActionBar
          count={selected.size}
          label="entry"
          onClear={() => setSelected(new Set())}
          onDelete={bulkDelete}
        />
        <div className="overflow-x-auto">
          <table className="aii-table">
            <thead><tr>
              <th className="w-8">
                <input type="checkbox" data-testid="rp-sel-all"
                  checked={rows.length > 0 && rows.every(r => selected.has(r.id))}
                  onChange={toggleSelectAll}/>
              </th>
              <th>Date</th>{isAdmin && <th>Member</th>}<th>Type</th><th>Method</th><th className="text-right">Amount</th><th>Description</th><th>Bill</th><th></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={isAdmin ? 9 : 8} className="text-center py-10 text-[color:var(--aii-muted)]">No entries yet.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} data-testid={`rp-row-${r.id}`} className={selected.has(r.id) ? "bg-[color:var(--aii-green-tint)]/40" : ""}>
                  <td><input type="checkbox" data-testid={`rp-sel-${r.id}`} checked={selected.has(r.id)} onChange={() => toggleSel(r.id)}/></td>
                  <td>{r.entry_date}</td>
                  {isAdmin && <td>{r.user_name} <span className="text-xs text-[color:var(--aii-muted)]">({r.user_role})</span></td>}
                  <td>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${r.kind === "receipt" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                      {r.kind === "receipt" ? <TrendingUp size={11} /> : <TrendingDown size={11} />} {r.kind}
                    </span>
                  </td>
                  <td className="uppercase text-xs">{r.method}</td>
                  <td className={`text-right font-semibold ${r.kind === "receipt" ? "text-emerald-700" : "text-red-700"}`}>
                    {r.kind === "receipt" ? "+" : "−"}{SYM[r.currency] || ""}{Number(r.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </td>
                  <td>{r.description || "—"}</td>
                  <td>{r.file_id ? <button onClick={() => openAttachment(r.file_id)} className="text-brand hover:underline text-xs inline-flex items-center gap-1"><Paperclip size={12} /> view</button> : <span className="text-[color:var(--aii-muted)] text-xs">—</span>}</td>
                  <td className="text-right">
                    {(r.user_id === user.id || isAdmin) && (
                      <button data-testid={`rp-del-${r.id}`} onClick={() => del(r.id)} className="text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}
